import * as T from '../domain/text';
import * as N from './nodes';
import * as FontFaceObserver from '@bannerflow/fontfaceobserver';
import { encodeHtml, deepEqual, omit, pick, cloneDeep, cloneMapDeep, delay } from '../../lib/utils';
import { EventEmitter } from '../../lib/event-emitter';
import { Color } from './color';
import { textShadowsCSSValue } from './css-styles';
import { IOffset } from '../domain/dimension';
import { ITextShadow } from '../domain/style';
import { IFontStyle } from '../domain/font';
import { getHorizontalFlexAlignment, getVerticalFlexAlignment, hasSameStyleProperty, forEachSpan, isContentSpan, createSpansFromString, hasSameStyleIds } from './text-nodes';
import { deserializePropertyValue, serializePropertyValue } from './serializer';
import { FeedStore } from './feed-store';
import { isElementDescendantOfElementWithClass } from '../../lib/dom-utils';
import { IActionRecorderService } from '../domain/services';
import { inject, __inject } from '../../lib/di';
import { T as S } from './rich-text.factories';
import { TextSelection } from './rich-text.selection.text';
import { IRichTextEditorService, IEditorState, IContentSpanSplit } from './rich-text.editor.header';
import { IRichText } from './rich-text.header';

export const STYLE_INDEX_MARGIN_RIGHT = 4;
export const FALLBACK_FONT_FAMILY = 'Helvetica, Arial, Tahoma, "Microsoft Yahei", 微软雅黑, STXihei, 华文细黑, sans-serif';

interface ISize {
    width: number;
    height: number;
}

interface ITypeContentStyle {
    type: T.SpanType;
    content: string;
    style?: Partial<T.ICharacterProperties>;
}

export interface ITextRange {
    startNode: Node;
    startOffset: number;
    endNode: Node;
    endOffset: number;
}

const enum ResizeAxis {
    Horziontal,
    Vertical,
}

const RESIZE_STEP_DIVIDER = 1.1;
const DEFAULT_RESIZE_STEP = 1;


interface ISpanDistributionMetadata {
    shouldAbort: boolean;
}

export interface IRichTextRenderOption {
    renderUnderscoreLayer?: boolean;
    element?: N.OneOfTextDataNodes;
    viewElement?: N.OneOfTextNodes;
    feedStore?: FeedStore;
    skipFontLoad?: boolean;
}

export type IStyle = Partial<T.ICharacterProperties>;

export class RichText extends EventEmitter<'selectionchange' | 'change' | 'editstart' | 'editend' | 'editburst' | 'variableselected' | 'hidevariablesettings' | 'focus' | 'blur' | 'interactionsStarted' | 'interactionsEnded' | 'destroyed' | 'historychange' | 'spanClicked'> implements IRichText {
    get selection(): TextSelection | undefined {
        return this.editor?.selection.selection;
    }
    set selection(selection: TextSelection | undefined) {
        if (this.editor) {
            this.editor.selection.selection = selection;
        }
    }
    currentContentLine?: T.IRenderedLine;
    textLineElementToLineMap: WeakMap<HTMLDivElement, number>;
    selectionSpanElements: HTMLSpanElement[] = [];
    lineToSpanDecorationLineElementMap = new Map<number, HTMLDivElement>();
    lineToSpanDecorationBackgroundLineElementMap = new Map<number, HTMLDivElement>();
    centerElements: HTMLDivElement[] = [];
    textLines: T.IRenderedLine[];
    textIntrospecter: HTMLDivElement;
    previousSpanType?: T.SpanType;
    lastBreakIndex?: number;
    scrollHeight: number;
    lineToTextLineElementMap: Map<number, HTMLDivElement>;

    /**
     * Stores the current longest line. The longest line excludes trailing spaces.
     */
    longestLine: number;

    /**
     * Resized element font size is the scaled element font size and not the original. When we resize to fit
     * text inside a box, we want to store the original font size and the resized(scaled) element font size.
     */
    resizedElementFontSize: number;
    fontSizeDiff: number;
    ellipsisWidth: number;
    reachedTruncationLine = false;
    spans: T.OneOfSpans[] = [];
    lastVerticalMargin = 0;
    wordWidth = 0;
    rows = 0;
    lastResizeAxis = ResizeAxis.Horziontal;
    stepValue = DEFAULT_RESIZE_STEP;
    lastHorizontalMargin = 0;
    widestWord = 0;
    textElement: HTMLDivElement;
    centerElement: HTMLDivElement;
    width: number;
    height: number;
    spanPositionLeft = 0;
    style: T.ITextProperties & Partial<ISize>;
    /** Used to keep track of which feed artifact that is clcked on */
    variableSpanMap = new Map<string, { span: T.OneOfContentSpans, style: Partial<T.ICharacterProperties> }>();
    snapshots: ISnapshot[] = [];
    redoSnapshots: ISnapshot[] = [];
    undoSnapshots: ISnapshot[] = [];
    firstSnapshotFromBurst?: ISnapshot;
    lastSnapshotTimestamp: number;
    pendingSnapshots = 0;
    blurSuspensionListeners: { element: HTMLElement, callback: (...args: any[]) => any }[] = [];
    actionRecorderService?: IActionRecorderService;
    zoom: number;

    renderUnderscoreLayer?: boolean;
    element?: N.OneOfTextDataNodes;
    viewElement?: N.OneOfTextNodes;

    initTask: Promise<void>;
    feedStore: FeedStore;
    redoIndex = 0;
    private skipFontLoad: boolean;

    constructor(
        public text: T.IText,
        public rootElement: HTMLDivElement,
        private option: IRichTextRenderOption,
        @inject(S.EDITOR_STATE, { optional: true })
        private editorState?: IEditorState,
        @inject(S.RICH_TEXT_EDITOR_SERVICE, { optional: true })
        public editor?: IRichTextEditorService)
    {
        super();
        this.text = text;
        this.style = this.text.style;
        this.syncProperties();
        this.renderTextContainers();

        this.renderUnderscoreLayer = option.renderUnderscoreLayer;
        this.element = option.element;
        this.viewElement = option.viewElement;
        this.feedStore = option.feedStore!;
        this.skipFontLoad = !!option.skipFontLoad;
    }

    async init(): Promise<void> {
        this.renderTextIntrospecter();
        this.initTask = new Promise<void>(async (resolve) => {
            await this.applyStylesToRootElement();
            const spans = this.initTextLayer();
            if (this.text.style.textOverflow === 'shrink') {
                this.resizeText(spans);
            }
            this.rerenderText();
            resolve();
        });
    }

    private renderTextContainers(): void {
        this.rootElement.style.backgroundColor = 'transparent';

        this.centerElement = document.createElement('div');
        this.centerElement.classList.add('center');
        this.centerElement.style.width = '100%';
        this.centerElement.style.height = '100%';
        this.centerElement.style.display = 'flex';
        this.centerElement.style.alignItems = getVerticalFlexAlignment(this.style.verticalAlignment);
        this.centerElement.style.justifyContent = getHorizontalFlexAlignment(this.style.horizontalAlignment);

        this.textElement = document.createElement('div');
        this.textElement.classList.add('text-content');
        this.textElement.style.outline = 'none';
        this.textElement.spellcheck = false;

        this.centerElement.appendChild(this.textElement);
        this.rootElement.appendChild(this.centerElement);
    }

    // TODO: Remove this to editor
    setText(text: T.IText, keepSelection?: boolean, reinitialize?: boolean): void {
        if (!keepSelection) {
            this.editor!.selection.clearSelection();
            this.selection = undefined;
        }
        this.text = text;
        this.spans = text.spans;
        if (reinitialize) {
            this.variableSpanMap = new Map();
        }
        else {
            this.normalizeTextLineSpans();
        }
        this.style = text.style;
        this.syncProperties();
        let expandedSpans: T.OneOfSpans[] = this.spans;
        if (!this.editor?.inInteractiveMode) {
            expandedSpans = this.expandVariableSpans(expandedSpans);
        }
        this.distributeAllSpansAcrossTextLines(expandedSpans);
        this.emit('change', [this.getText()]);
        this.rerender();
    }

    preventBlur = (event: MouseEvent): void => {
        if (event.target) {
            if (isElementDescendantOfElementWithClass(event.target, 'prevent-text-blur')) {
                this.editor!.__suspendNextBlur = true;
            }
            if ((event.target as HTMLElement).tagName === 'TEXTAREA' || (event.target as HTMLElement).tagName === 'INPUT') {
                this.editor!.__suspendNextBlur = true;
            }
        }
    }

    // TODO: This is not fired when swapping language, potential memory leak.
    destroy(): void {
        if (this.editor?.inInteractiveMode) {
            this.editor.stopEdit();
        }
        this.blurSuspensionListeners = [];
        for (const blurSuspension of this.blurSuspensionListeners) {
            blurSuspension.element.removeEventListener('mousedown', blurSuspension.callback);
        }
        this.removeTextIntrospecter();
        if (this.editor) {
            window.removeEventListener('mouseup', this.editor.mouseBindings.onMouseUp);
            window.removeEventListener('mousemove', this.editor.mouseBindings.onMouseMove);
        }
        this.emit('destroyed');
        this.clearEvents();
    }

    blur(): void {
        if (this.editor!.keyboardBindings.textarea) {
            this.editor!.keyboardBindings.textarea.blur();
        }
    }

    private syncProperties(): void {
        const computedStyle = getComputedStyle(this.rootElement);
        if (!this.style.fontSize) {
            this.style.fontSize = parseInt(computedStyle.fontSize!, 10);
        }
        if (!this.style.textColor) {
            this.style.textColor = Color.parse(computedStyle.color!);
        }
        if (!this.style.lineHeight) {
            this.style.lineHeight = parseFloat(computedStyle.lineHeight!);
        }
        if (!this.style.padding) {
            const padding: IOffset = { top: 0, left: 0, bottom: 0, right: 0 };
            if (computedStyle.paddingTop) {
                padding.top = parseInt(computedStyle.paddingTop!, 10);
            }
            if (computedStyle.paddingBottom) {
                padding.bottom = parseInt(computedStyle.paddingBottom!, 10);
            }
            if (computedStyle.paddingLeft) {
                padding.left = parseInt(computedStyle.paddingLeft!, 10);
            }
            if (computedStyle.paddingRight) {
                padding.right = parseInt(computedStyle.paddingRight!, 10);
            }
            this.style.padding = padding;
        }
        if (!this.style.width || !this.style.height) {
            const boundingRect = this.rootElement.getBoundingClientRect();
            this.width = boundingRect.width;
            this.height = boundingRect.height;
        }
        else {
            this.width = this.style.width;
            this.height = this.style.height;
        }
    }

    rerender(element?: N.ITextNode): void {
        if (element) {
            this.width = element.width;
            this.height = element.height;
            for (const property of N.textProperties) {
                this.style[property as string /*TS bug?*/] = element[property];
            }
        }

        if (this.textLineElementToLineMap) {
            this.lineToTextLineElementMap.forEach(line => {
                line.removeEventListener('click', this.onClick);
                (Array.from(line.children) as HTMLElement[]).forEach((child) => {
                    child.removeEventListener('click', this.onClick);
                });
            });
        }

        if (this.selection && this.editor?.inInteractiveMode) {
            this.editor.selection.storeSelectionCharacterPositions();
        }
        let expandedSpans = this.spans;
        if (!this.editor?.inInteractiveMode && !this.renderUnderscoreLayer) {
            expandedSpans = this.expandVariableSpans(expandedSpans);
        }
        this.distributeAllSpansAcrossTextLines(expandedSpans);
        if (this.style.textOverflow === 'shrink') {
            this.resizeText(expandedSpans);
        }
        this.rerenderText();
        if (this.selection && this.editor?.inInteractiveMode) {
            this.editor.selection.reselectText();
        }
    }

    getTextProperties(): Partial<T.ITextElementProperties & T.IMixedCharacterProperties> {
        if (this.editor!.selection.isElementSelected()) {
            const textProperties = Object.assign({}, this.style);
            const font = textProperties.font as IFontStyle;
            textProperties.__fontFamilyId = font && font.fontFamilyId;
            return textProperties;
        }

        // If collapsed we want to return current style instead of intersection style, because the user can
        // erase text backwards and it should preserve the style before the erasure.
        if (this.selection!.isCollapsed) {
            const textProperties = Object.assign({}, this.style, this.editor!.currentStyle);
            const font = textProperties.font;
            textProperties.__fontFamilyId = font && font.fontFamilyId;
            return textProperties;
        }
        const spanIntersectionStyle = this.getIntersectionStyleFromSelection(/*excludeMixedValues*/false);
        if (Object.keys(spanIntersectionStyle).length === 0) {
            return Object.assign({}, this.style, this.style.font ? { __fontFamilyId: this.style.font.fontFamilyId } : {});
        }
        return Object.assign({}, this.style, spanIntersectionStyle);
    }

    private getIntersectionStyleFromSelection(excludeMixedValues: boolean): Partial<T.ITextElementProperties & T.IMixedCharacterProperties> {
        const style = this.getPerPropertyStyleValues();
        return this.flattenSameStyles(style, excludeMixedValues);
    }

    getPerPropertyStyleValues(): Map<keyof T.ICharacterProperties, Set<string>> {
        const style = new Map<keyof T.ICharacterProperties, Set<string>>();
        if (!this.selection) {
            return style;
        }
        this.editor!.forEachSpanInSelection((span) => {
            if (span.type === T.SpanType.Newline || span.type === T.SpanType.End) {
                return;
            }
            for (const property of N.textProperties) {
                let value = style.get(property as keyof T.ICharacterProperties);
                if (value === undefined) {
                    value = new Set<any>();
                    style.set(property as keyof T.ICharacterProperties, value);
                }
                let spanStyleValue: any;
                if (property === 'fontSize' && span.style[property]) {
                    spanStyleValue = Math.round((span.style[property] as number) * this.style.fontSize! * 100) / 100;
                }
                else if (property === '__fontFamilyId') {
                    spanStyleValue = (span.style.font && span.style.font.fontFamilyId) || (this.style.font && this.style.font.fontFamilyId);
                }
                else {
                    spanStyleValue = span.style[property] || this.style[property];
                }
                value.add(spanStyleValue && serializePropertyValue(property, spanStyleValue)!);
            }
        });
        return style;
    }

    private flattenSameStyles(style: Map<keyof T.ICharacterProperties, Set<any>>, excludeMixedValues: boolean): Partial<T.ICharacterProperties> {
        const flattenedStyle: Partial<T.ICharacterProperties> = {};
        for (const [property, value] of style.entries()) {
            if (value.size === 1) {
                const v = Array.from(value)[0];
                if (v && property === 'variable') {
                    this.setStyle(property, v, flattenedStyle);
                }
                else {
                    this.setStyle(property, v && deserializePropertyValue(property, v) as any, flattenedStyle);
                }
                continue;
            }
            else if (value.size > 1 && (property === 'font' || property === '__fontFamilyId')) {
                this.setStyle(property, '$mixed', flattenedStyle);
                continue;
            }
            if (!excludeMixedValues) {
                flattenedStyle[property] = undefined;
            }
        }
        return flattenedStyle;
    }

    getSpansFromTextLines(): T.OneOfSpans[] {
        const spans: T.OneOfSpans[] = [];
        for (const textLine of this.textLines) {
            for (const span of textLine.spans) {
                if (span.type === T.SpanType.Ellipsis) {
                    throw new Error('Ellipsis cannot exist in this stage.');
                }
                spans.push(span);
            }
        }
        return spans;
    }

    private linearlyScaleText(width: number, height: number): void {
        const widthOccupation = this.longestLine !== 0 && this.width !== 0
            ? this.longestLine / width
            : 0;
        const heightOccuptation = this.scrollHeight !== 0 && this.height !== 0
            ? this.scrollHeight / height
            : 0;
        if (heightOccuptation > widthOccupation) {
            this.resizedElementFontSize = this.resizedElementFontSize / heightOccuptation;
            this.scaleTextLineMetadata(1 / heightOccuptation);
        }
        else {
            this.resizedElementFontSize = this.resizedElementFontSize / widthOccupation;
            this.scaleTextLineMetadata(1 / widthOccupation);
        }
    }

    private scaleTextLineMetadata(scale: number): void {
        for (const textLine of this.textLines) {
            textLine.lineHeight *= scale;
            textLine.lineWidth *= scale;
            textLine.maxFontSize *= scale;
            for (const span of textLine.spans) {
                span.width *= scale;
                span.lineHeight *= scale;
            }
        }
    }

    resizeText(spans: T.OneOfSpans[]): void {
        const elementHeight = Math.max(this.height - this.style.padding!.top! - this.style.padding!.bottom!, 0);
        const elementWidth = Math.max(this.width - this.style.padding!.left! - this.style.padding!.right!, 0);

        // Text resize doesn't work with too small element size.
        if (elementHeight < 3 || elementWidth < 3) {
            this.resizedElementFontSize = 0;
            return;
        }
        const ratio = elementWidth / this.widestWord;
        const maxFontSize = Math.min(this.resizedElementFontSize * ratio, this.style.fontSize!);
        const resizeOccupationFactors = new Map<number/*resizedElementFontSize*/, number/*occupationFactor*/>();
        const sameResizeEncounters = new Set<number/*resizedElementFontSize*/>();
        if (ratio < 1) {
            this.resizedElementFontSize = this.resizedElementFontSize * ratio;
            this.fontSizeDiff = ratio;
            this.redistributeAllSpansAcrossLines(spans);
        }

        let n = 0;
        do {
            const widthOccupation = this.longestLine !== 0 && elementWidth !== 0
                ? this.longestLine / elementWidth
                : 0;
            const heightOccupation = this.scrollHeight !== 0 && elementHeight !== 0
                ? this.scrollHeight / elementHeight
                : 0;

            const occupationFactor = Math.abs((widthOccupation - 1) * (heightOccupation - 1));

            if (resizeOccupationFactors.has(this.resizedElementFontSize)) {
                sameResizeEncounters.add(this.resizedElementFontSize);
                if (sameResizeEncounters.size >= 2) {
                    let minOccupation = Infinity;
                    let minOccupationResizedElementFontSize = 0;
                    for (const resizedElementFontSize of sameResizeEncounters) {
                        const occupationFactor = resizeOccupationFactors.get(resizedElementFontSize)!;
                        if (occupationFactor < minOccupation) {
                            minOccupation = occupationFactor;
                            minOccupationResizedElementFontSize = resizedElementFontSize;
                        }
                    }
                    this.resizedElementFontSize = minOccupationResizedElementFontSize;
                    this.redistributeAllSpansAcrossLines(spans);
                    break /*linearlyScaleTheText*/;
                }
            }
            else {
                resizeOccupationFactors.set(this.resizedElementFontSize, occupationFactor);
            }
            if (heightOccupation > widthOccupation) {
                const verticalMargin = elementHeight - this.scrollHeight;
                if (verticalMargin <= 1 && verticalMargin >= 0) {
                    this.resetResizeMetadata();
                    return;
                }
                if (this.lastResizeAxis === ResizeAxis.Horziontal) {
                    this.decreseStepValue();
                }
                if (verticalMargin < 0) {
                    if (this.lastResizeAxis === ResizeAxis.Vertical && this.lastVerticalMargin > 0) {
                        this.decreseStepValue();
                    }
                    this.fontSizeDiff = (this.resizedElementFontSize - this.stepValue) / this.resizedElementFontSize;
                    this.resizedElementFontSize = this.resizedElementFontSize - this.stepValue;
                }
                else {
                    if (this.resizedElementFontSize + this.stepValue > maxFontSize) {
                        this.resetResizeMetadata();
                        return;
                    }
                    if (this.lastResizeAxis === ResizeAxis.Vertical && this.lastVerticalMargin < 0) {
                        this.decreseStepValue();
                    }
                    this.fontSizeDiff = (this.resizedElementFontSize + this.stepValue) / this.resizedElementFontSize;
                    this.resizedElementFontSize = this.resizedElementFontSize + this.stepValue;
                }
                this.redistributeAllSpansAcrossLines(spans);
                this.lastResizeAxis = ResizeAxis.Vertical;
                this.lastVerticalMargin = verticalMargin;
            }
            else if (widthOccupation > heightOccupation) {
                const horizontalMargin = elementWidth - this.longestLine;
                if (this.resizedElementFontSize + this.stepValue > maxFontSize && horizontalMargin >= 0) {
                    this.resetResizeMetadata();
                    return;
                }
                if (horizontalMargin <= 1 && horizontalMargin >= 0) {
                    this.resetResizeMetadata();
                    return;
                }
                if (this.lastResizeAxis === ResizeAxis.Vertical) {
                    this.decreseStepValue();
                }
                if (horizontalMargin < 0) {
                    if (this.lastResizeAxis === ResizeAxis.Horziontal && this.lastHorizontalMargin > 0) {
                        this.decreseStepValue();
                    }
                    this.fontSizeDiff = (this.resizedElementFontSize - this.stepValue) / this.resizedElementFontSize;
                    this.resizedElementFontSize = this.resizedElementFontSize - this.stepValue;
                }
                else {
                    if (this.resizedElementFontSize + this.stepValue > maxFontSize) {
                        return;
                    }
                    if (this.lastResizeAxis === ResizeAxis.Horziontal && this.lastHorizontalMargin < 0) {
                        this.decreseStepValue();
                    }
                    this.fontSizeDiff = (this.resizedElementFontSize + this.stepValue) / this.resizedElementFontSize;
                    this.resizedElementFontSize = this.resizedElementFontSize + this.stepValue;
                }
                this.redistributeAllSpansAcrossLines(spans);
                this.lastResizeAxis = ResizeAxis.Vertical;
                this.lastHorizontalMargin = horizontalMargin;
            }
            else {
                return;
            }
            n++;
        }
        while (n < 1000);

        this.linearlyScaleText(elementWidth, elementHeight);
    }

    private decreseStepValue(): void {
        this.stepValue = Math.max(0.1, this.stepValue / RESIZE_STEP_DIVIDER);
    }

    private resetResizeMetadata(): void {
        this.lastHorizontalMargin = 0;
        this.lastVerticalMargin = 0;
        this.stepValue = DEFAULT_RESIZE_STEP;
    }

    setStyle<K extends keyof T.ITextElementCharacterProperties>(property: K, value: T.ITextElementCharacterProperties[K], style: Partial<T.ITextElementCharacterProperties>): void {
        if (hasSameStyleProperty(property, { [property]: value }, this.style)) {
            if (style !== this.style) {
                delete style[property];
            }
        }
        else {
            if (value === '$mixed') {
                style[property] = value;
                return;
            }
            const unit = N.characterPropertyToUnitMap[property];
            switch (unit) {
                case 'Color':
                    style.textColor = (value as Color).copy() as Color;
                    break;
                case 'FontStyle':
                    style[property] = Object.assign({}, value);
                    break;
                case 'Variable':
                    style[property] = Object.assign({}, value);
                    break;
                case 'TextShadow[]':
                    style.textShadows = value && (value as ITextShadow[]).map((item) => Object.assign({}, item));
                    break;
                case 'boolean':
                    if (typeof value !== 'boolean') {
                        delete style[property];
                    }
                    else {
                        style[property] = value;
                    }
                    break;
                default:
                    style[property] = value;
                    break;
            }
        }
    }

    getText(): T.IText {
        return {
            style: this.style,
            spans: this.spans,
        };
    }

    splitSpan(span: T.OneOfContentSpans, column: number): IContentSpanSplit {
        const preContent = span.type === T.SpanType.Variable ? '' : span.content.slice(0, column);
        const postContent = span.type === T.SpanType.Variable ? span.content : span.content.slice(column);
        const spans = {} as IContentSpanSplit;
        spans.preContentSpan = {
            type: span.type,
            content: preContent,
            style: this.copyStyle(span.style),
            attributes: { styleIndex: span.attributes.styleIndex, shouldRenderNumber: false },
            styleIds: cloneMapDeep(span.styleIds),
            styleId: span.styleId,
            __previousStyleIds: span.__previousStyleIds && [...span.__previousStyleIds],
            __previousStyleIdToHistoryIndexMap: span.__previousStyleIdToHistoryIndexMap && new Map(span.__previousStyleIdToHistoryIndexMap),
        } as T.IWordSpan | T.ISpaceSpan;
        spans.postContentSpan = {
            type: span.type,
            content: postContent,
            style: this.copyStyle(span.style),
            attributes: { styleIndex: span.attributes.styleIndex, shouldRenderNumber: span.attributes.shouldRenderNumber },
            styleIds: cloneMapDeep(span.styleIds),
            styleId: span.styleId,
            __previousStyleIds: span.__previousStyleIds && [...span.__previousStyleIds],
            __previousStyleIdToHistoryIndexMap: span.__previousStyleIdToHistoryIndexMap && new Map(span.__previousStyleIdToHistoryIndexMap),
        } as T.IWordSpan | T.ISpaceSpan;
        return spans;
    }

    removeDecorationLayers(): void {
        let centerElement: HTMLDivElement | undefined;
        while (centerElement = this.centerElements.pop()) {
            if (this.rootElement.contains(centerElement)) {
                this.rootElement.removeChild(centerElement);
            }
        }
    }

    async applyStylesToRootElement(): Promise<void> {
        this.textLines = [];

        // Don't try to preload fonts if there are no uploaded fonts
        if (!this.skipFontLoad && this.style.font) {
            try {
                const isHtmlExport = this.style.font.src.startsWith('data');
                if (isHtmlExport) {
                    await delay(400);
                }
                // @@remove STUDIO:START
                else if (process.env.STUDIO_JS) {
                    const fontLoads: Promise<any>[] = [];
                    const font = new FontFaceObserver(this.style.font.id);
                    fontLoads.push(font.load());
                    if (this.editorState) {
                        const fontStylesInSpans = this.getAllFontsInSpans();
                        for (const fontStyleId of fontStylesInSpans.values()) {
                            const font = new FontFaceObserver(fontStyleId);
                            fontLoads.push(font.load());
                        }
                    }
                    await Promise.all(fontLoads);
                }
                // @@remove STUDIO:END
            }
            catch (e) {
                console.error('Could not load font face:', this.style.font.id);
                console.error(e);
            }
        }

        const element = this.rootElement;
        const font = getFontStyleCSS(this.style.font, element);
        Object.assign(element.style, font);
        element.style.fontSize = this.style.fontSize ? this.style.fontSize + 'px' : '';
    }

    private getAllFontsInSpans(): Set<string/*fontStyleId*/> {
        const fontStyleIds = new Set<string>();
        for (const span of this.text.spans) {
            if (isContentSpan(span)) {
                const styleId = span.styleIds.get(this.editorState!.document!.id)!;
                const style = this.option.element!.characterStyles.get(styleId)!;
                if (style && style.font) {
                    fontStyleIds.add(style.font.id);
                }
            }
        }
        return fontStyleIds;
    }

    initTextLayer(): T.OneOfSpans[] {
        this.renderTextIntrospecter();
        this.spans = this.text.spans;
        let expandedSpans: T.OneOfSpans[] = this.spans;
        if (!this.editor?.inInteractiveMode) {
            expandedSpans = this.expandVariableSpans(expandedSpans);
        }
        this.distributeAllSpansAcrossTextLines(expandedSpans);
        this.removeDecorationLayers();
        this.renderTextLines();
        return expandedSpans;
    }

    renderTextIntrospecter(): void {
        if (!this.textIntrospecter) {
            this.textIntrospecter = document.createElement('div');
            this.textElement.ownerDocument!.body.appendChild(this.textIntrospecter);
            this.textIntrospecter.style.position = 'absolute';
            this.textIntrospecter.style.top = '10000px';
            // DEBUG: Uncomment this line to debug
            // this.textIntrospecter.style.top = '0px';
        }

        // Get default font from rootElement and not the introspecter since canvas have other font than body
        const font = getFontStyleCSS(this.style.font, this.rootElement);
        Object.assign(this.textIntrospecter.style, font);
        this.textIntrospecter.style.fontSize = this.style.fontSize ? this.style.fontSize + 'px' : '';
    }

    private removeTextIntrospecter(): void {
        if (this.textIntrospecter && this.textIntrospecter.parentNode) {
            this.textIntrospecter.parentNode.removeChild(this.textIntrospecter);
        }
    }

    copyStyle = (style: Partial<T.ICharacterProperties>): T.ICharacterProperties => {
        const styleCopy = {} as T.ICharacterProperties;
        for (const property in style) {
            switch (property) {
                case 'textColor':
                    styleCopy[property] = (style[property] as Color).copy();
                    break;
                case 'fontSize':
                    styleCopy[property] = style[property] as number;
                    break;
                default:
                    styleCopy[property] = style[property];
                    break;
            }
        }
        return styleCopy;
    }

    private splitWordOverflowedSpan(span: T.OneOfContentSpans): IContentSpanSplit {
        const spanElement = this.appendSpanToIntrospecter(span);
        const textNode = spanElement.firstChild as Text;
        if (!textNode) {
            throw new Error('Could not get text node.');
        }
        if (!this.currentContentLine) {
            throw new Error('Current content line is not set.');
        }
        const lastOffset = span.content.length;
        for (let offset = 1; offset <= span.content.length; offset++) {
            const selectionRange = new Range();
            selectionRange.setStart(textNode, 0);
            selectionRange.setEnd(textNode, offset);
            const boundingRect = selectionRange.getBoundingClientRect();
            if (boundingRect) {
                if (this.wordWidth + boundingRect.width + ((offset === lastOffset && span.attributes.shouldRenderNumber) ? STYLE_INDEX_MARGIN_RIGHT : 0) + 1 /* '1' is error margin */ > this.width - this.style.padding!.left - this.style.padding.right) {
                    return this.splitSpan(span, offset - 1);
                }
            }
        }
        throw new Error('Could not split span.');
    }

    private emptyTextLines(textLines: T.IRenderedLine[]): void {
        this.currentContentLine = {
            spans: [{ type: T.SpanType.End, content: 'END', top: 0, left: 0, width: 0, height: 0, lineHeight: 0, attributes: {} }],
            lineHeight: 0,
            maxFontSize: 1,
            lineWidth: 0,
            characterWidth: 0,
            trailingSpaceWidth: 0,
            endsWithNewline: false,
            isLastLine: false,
        };
        textLines.splice(0, Infinity, this.currentContentLine);
    }

    private truncateCurrentContentLine(textLines: T.IRenderedLine[]): void {
        let lineWidth = 0;
        const spans: T.OneOfRenderedSpans[] = [];
        outer: for (let i = 0; i < this.currentContentLine!.spans.length; i++) {
            const span = this.currentContentLine!.spans[i];
            if (span.type === T.SpanType.Ellipsis) {
                throw new Error('Unexpected ellipsis span.');
            }
            if (span.type === T.SpanType.End) {
                break;
            }
            if (span.type === T.SpanType.Newline) {
                spans.push({ type: T.SpanType.Ellipsis, content: '...', top: 0, left: 0, width: 0, height: 0, lineHeight: 0, attributes: {} });
                break;
            }
            const spanElement = this.appendSpanToIntrospecter(span);
            const textNode = spanElement.firstChild as Text | null;

            // If text node is null, it indicates that the first character in some row is wider
            // than the the elements width. In which case, we should not display any characters at all.
            if (!textNode) {
                this.emptyTextLines(textLines);
                return;
            }

            for (let offset = 0; offset <= span.content.length; offset++) {
                const selectionRange = new Range();
                selectionRange.setStart(textNode, 0);
                selectionRange.setEnd(textNode, offset);
                const boundingRect = selectionRange.getBoundingClientRect();
                if (boundingRect && lineWidth + boundingRect.width + this.ellipsisWidth > this.width - this.style.padding.top * 2) {
                    if (offset === 0) {

                        // If ellipses is wider than the element width. Don't show anything.
                        this.emptyTextLines(textLines);
                        return;
                    }
                    const { preContentSpan } = this.splitSpan(span, offset - 1);
                    preContentSpan.width = boundingRect.width;
                    preContentSpan.lineHeight = boundingRect.height;
                    spans.push(preContentSpan, { type: T.SpanType.Ellipsis, content: '...', top: 0, left: 0, width: 0, height: 0, lineHeight: 0, attributes: {} });
                    break outer;
                }
            }
            spans.push(span);
            lineWidth += span.width;
        }
        this.currentContentLine!.spans = spans;
        this.recalculateCurrentContentLineMetadata();
    }

    private distributeOneSpanAcrossTextLines = (span: T.OneOfSpans, textLines: T.IRenderedLine[], metadata: ISpanDistributionMetadata, useDom: boolean = true, restOfSpans: T.OneOfSpans[]): void => {
        const self = this;
        if (!this.currentContentLine) {
            this.currentContentLine = {
                spans: [],
                lineHeight: 0,
                lineWidth: 0,
                maxFontSize: 1,
                characterWidth: 0,
                trailingSpaceWidth: 0,
                endsWithNewline: false,
                isLastLine: false,
            };
            textLines.push(this.currentContentLine);
            this.spanPositionLeft = 0;
        }
        const spanSize = this.introspectSpan(span, useDom);
        const fontSize = (span as T.OneOfContentSpans).style && (span as T.OneOfContentSpans).style.fontSize || this.resizedElementFontSize;
        const lineHeight = (((span as T.OneOfContentSpans).style && (span as T.OneOfContentSpans).style.lineHeight) || this.style.lineHeight);

        span.top = (lineHeight - 1) * fontSize / 2;
        span.left = this.spanPositionLeft;
        span.width = spanSize.width;
        span.height = spanSize.height - (lineHeight - 1) * fontSize;
        span.lineHeight = spanSize.height;

        let charSpacing = 0;
        if (span.type === T.SpanType.Word) {
            const characterSpacing = span.style.characterSpacing! || this.style.characterSpacing || 0;
            charSpacing = fontSize * characterSpacing / 10;
        }
        if (span.type === T.SpanType.End) {
            appendEndSpan(span);
            return;
        }
        if (span.type === T.SpanType.Newline) {
            appendNewlineSpan(span);
            return;
        }
        if ((this.style.textOverflow === 'truncate' ||
            this.style.textOverflow === 'expand') &&
            (span.type === T.SpanType.Word || span.type === T.SpanType.Variable) &&
            this.wordWidth + span.width + (span.attributes.shouldRenderNumber ? STYLE_INDEX_MARGIN_RIGHT : 0) - charSpacing > this.width - this.style.padding.left - this.style.padding.right)
        {
            handleWordsBiggerThanWidthOfElement(span);
            return;
        }
        if ((span.type === T.SpanType.Word || span.type === T.SpanType.Variable) &&
            this.previousSpanType === T.SpanType.Space)
        {
            this.lastBreakIndex = this.currentContentLine.spans.length;
        }
        const relativeFontSize = span.style.fontSize ? this.resizedElementFontSize * span.style.fontSize : this.resizedElementFontSize;
        const characterSpacing = span.style.characterSpacing! || this.style.characterSpacing || 0;
        if (this.lastBreakIndex !== undefined &&
            span.type !== T.SpanType.Space && span.type !== T.SpanType.Composition &&
            this.spanPositionLeft + span.width - (characterSpacing * relativeFontSize) /* ending char spacing should not be breakable*/ >= this.width - this.style.padding.left - this.style.padding.right &&
            this.currentContentLine.spans.length !== 0)
        {
            appendSpansThatExceedsCurrentContentLine(span);
        }
        else {
            appendSpanToCurrentContentLine(span);
        }

        function appendSpanToCurrentContentLine(span: T.OneOfContentSpans): void {
            self.currentContentLine!.lineWidth += span.width;
            self.currentContentLine!.spans.push(span);
            if (span.type === T.SpanType.Word || span.type === T.SpanType.Variable) {
                self.wordWidth += span.width + (span.attributes.shouldRenderNumber ? STYLE_INDEX_MARGIN_RIGHT : 0);
                if (self.wordWidth > self.widestWord) {
                    self.widestWord = self.wordWidth;
                }
            }
            else {
                self.wordWidth = 0;
            }
            saveSpanPositionLeftAndSpanType();
        }

        function saveSpanPositionLeftAndSpanType(): void {
            self.spanPositionLeft += span.width;
            if (span.attributes && span.attributes.shouldRenderNumber) {
                self.spanPositionLeft += STYLE_INDEX_MARGIN_RIGHT;
            }
            self.previousSpanType = span.type;
        }

        function appendSpansThatExceedsCurrentContentLine(span: T.OneOfContentSpans): void {
            const belowMaxRows = !self.style.maxRows || self.rows < self.style.maxRows!;
            if ((self.editor?.inInteractiveMode && self.style.textOverflow === 'truncate') || belowMaxRows) {
                const spinOffSpans = self.currentContentLine!.spans.splice(self.lastBreakIndex!, Infinity);
                self.recalculateCurrentContentLineMetadata();
                const nextLineSpans = spinOffSpans.concat([span]);
                self.addScrollHeight(textLines, metadata, (nextLineSpans as T.OneOfSpans[]).concat(restOfSpans));
                if (self.reachedTruncationLine) {
                    return;
                }
                self.currentContentLine = {
                    spans: [],
                    lineHeight: 0,
                    maxFontSize: 1,
                    lineWidth: 0,
                    trailingSpaceWidth: 0,
                    characterWidth: 0,
                    endsWithNewline: false,
                    isLastLine: false,
                };
                textLines.push(self.currentContentLine);
                self.rows++;
                if (!self.editor?.inInteractiveMode && self.style.textOverflow === 'truncate') {
                    self.wordWidth = 0; // All spans are redacted and redistributed. So word width should be zero.
                    for (let i = 0; i < nextLineSpans.length; i++) {
                        if (self.reachedTruncationLine) {
                            return;
                        }
                        self.distributeOneSpanAcrossTextLines(nextLineSpans[i] as T.OneOfSpans, textLines, metadata, useDom, (nextLineSpans.slice(i + 1) as T.OneOfSpans[]).concat(restOfSpans));
                    }
                    self.recalculateCurrentContentLineMetadata();
                }
                else {
                    self.wordWidth += span.width + (span.attributes.shouldRenderNumber ? STYLE_INDEX_MARGIN_RIGHT : 0); // The previous span widths are already added, so just add the last one.
                    if (self.wordWidth > self.widestWord) {
                        self.widestWord = self.wordWidth;
                    }
                    self.currentContentLine.spans.push(...nextLineSpans);
                    self.recalculateCurrentContentLineMetadata();
                }
            }
            else {
                self.currentContentLine!.spans.push(span);
                self.recalculateCurrentContentLineMetadata();
                if (!self.editor?.inInteractiveMode && self.style.textOverflow === 'truncate') {
                    self.addSpansAndTruncateCurrentContentLine(textLines, restOfSpans, metadata);
                }
            }
            self.lastBreakIndex = undefined;
            saveSpanPositionLeftAndSpanType();
        }

        function handleWordsBiggerThanWidthOfElement(span: T.OneOfContentSpans): void {
            const { preContentSpan, postContentSpan } = self.splitWordOverflowedSpan(span);
            const newRestOfSpans = [postContentSpan as T.OneOfSpans].concat(restOfSpans);
            if (preContentSpan.content) {
                // Split word overflow spans needs to be redistributed because they are being split by word width and not
                // the current left span position. Also truncation can be reached!.
                self.distributeOneSpanAcrossTextLines(preContentSpan, textLines, metadata, /*useDom*/ true, newRestOfSpans);
                if (self.reachedTruncationLine) {
                    return;
                }
            }

            // if empty pre-content and word width is zero, we cannot fit the first character of span. Thus, we
            // should just show empty text element.
            else if (self.wordWidth === 0) {
                self.reachedTruncationLine = true;
                metadata.shouldAbort = true;
                return self.emptyTextLines(textLines);
            }
            self.recalculateCurrentContentLineMetadata();
            self.addScrollHeight(textLines, metadata, newRestOfSpans);
            if (self.reachedTruncationLine) {
                return;
            }
            const belowMaxRows = !self.style.maxRows || self.rows < self.style.maxRows!;
            if (self.editor?.inInteractiveMode || belowMaxRows) {
                self.currentContentLine = {
                    spans: [],
                    lineHeight: 0,
                    lineWidth: 0,
                    maxFontSize: 1,
                    characterWidth: 0,
                    trailingSpaceWidth: 0,
                    endsWithNewline: false,
                    isLastLine: false,
                };
                self.rows++;
                self.wordWidth = 0;
                textLines.push(self.currentContentLine);
                self.distributeOneSpanAcrossTextLines(postContentSpan, textLines, metadata, /*useDom*/ true, restOfSpans);
            }
            else {
                self.addSpansAndTruncateCurrentContentLine(textLines, restOfSpans, metadata);
            }
        }

        function appendNewlineSpan(span: T.INewlineSpan): void {
            self.previousSpanType = T.SpanType.Newline;
            if (!self.style.maxRows || self.rows < self.style.maxRows!) {
                self.currentContentLine!.spans.push(span);
                self.recalculateCurrentContentLineMetadata();
                self.currentContentLine!.endsWithNewline = true;
                self.addScrollHeight(textLines, metadata, restOfSpans);
                if (self.reachedTruncationLine) {
                    return;
                }
                if (!self.style.maxRows || self.rows < self.style.maxRows!) {
                    self.currentContentLine = {
                        spans: [],
                        lineHeight: 0,
                        maxFontSize: 1,
                        lineWidth: 0,
                        characterWidth: 0,
                        trailingSpaceWidth: 0,
                        endsWithNewline: false,
                        isLastLine: false,
                    };
                    self.wordWidth = 0;
                    textLines.push(self.currentContentLine);
                    self.rows++;
                }
                self.lastBreakIndex = undefined;
            }
        }

        function appendEndSpan(span: T.IEndSpan): void {
            self.currentContentLine!.spans.push(span);
            self.recalculateCurrentContentLineMetadata();
            self.currentContentLine!.isLastLine = true;
            self.addScrollHeight(textLines, metadata, restOfSpans);
            if (self.reachedTruncationLine) {
                return;
            }
            self.lastBreakIndex = undefined;
        }
    }

    private addSpansAndTruncateCurrentContentLine(textLines: T.IRenderedLine[], restOfSpans: T.OneOfSpans[], metadata: ISpanDistributionMetadata): void {
        this.currentContentLine!.spans.push(...restOfSpans);
        this.recalculateCurrentContentLineMetadata();
        this.truncateCurrentContentLine(textLines);
        this.reachedTruncationLine = true;
        metadata.shouldAbort = true;
    }

    private recalculateCurrentContentLineMetadata(): void {
        if (!this.currentContentLine) {
            return;
        }
        let lineHeight = 0;
        let maxFontSize = 0; // Use relative font size
        let lineWidth = 0;
        let characterWidth = 0;
        let endingSpaceWidth = 0;
        let left = 0;
        let isFirstSpanInLine = true;
        for (const span of this.currentContentLine.spans) {
            if (span.type === T.SpanType.End) {
                if (this.currentContentLine.spans.length === 1) {
                    lineHeight = (this.editor?.currentStyle.lineHeight || this.style.lineHeight) * this.resizedElementFontSize;
                }
                continue;
            }

            // Newline should not contribute to line height when it is not the first
            if (span.type !== T.SpanType.Newline) {
                if ((span.type !== T.SpanType.Space || isFirstSpanInLine) && span.lineHeight > lineHeight) {
                    lineHeight = span.lineHeight;
                }
            }
            else {

                // Only contribute to line height when the newline is first(or only span in line, which is the same).
                if (isFirstSpanInLine && span.lineHeight > lineHeight) {
                    lineHeight = span.lineHeight;
                }
                lineWidth += span.width;
                endingSpaceWidth += span.width;
                characterWidth += 1;
                continue;
            }
            if (span.type === T.SpanType.Ellipsis) {
                endingSpaceWidth = 0;
                continue;
            }
            span.left = left;
            const characterSpacing = span.style.characterSpacing || this.style.characterSpacing || 0;
            const fontSize = span.style.fontSize ? span.style.fontSize * this.resizedElementFontSize : this.resizedElementFontSize;
            if (span.type === T.SpanType.Word) {
                endingSpaceWidth = characterSpacing * fontSize;
            }
            else {
                endingSpaceWidth = 0;
            }
            if (span.type === T.SpanType.Space) {
                endingSpaceWidth += span.width;
            }
            lineWidth += span.width;
            if (span.style.fontSize && span.style.fontSize > maxFontSize) {
                maxFontSize = span.style.fontSize; // Is relative to element font size
            }
            characterWidth += span.content.length;
            left += span.width;
            if (span.attributes.shouldRenderNumber) {
                left += STYLE_INDEX_MARGIN_RIGHT;
            }
            isFirstSpanInLine = false;
        }

        this.currentContentLine.lineWidth = lineWidth;
        this.currentContentLine.trailingSpaceWidth = endingSpaceWidth;
        this.currentContentLine.lineHeight = lineHeight;
        this.currentContentLine.maxFontSize = maxFontSize || 1;
        this.currentContentLine.characterWidth = characterWidth;
        if (lineWidth - endingSpaceWidth > this.longestLine) {
            this.longestLine = Math.round((lineWidth - endingSpaceWidth) * 100) / 100;
        }
        this.spanPositionLeft = 0;
    }

    private addScrollHeight(textLines: T.IRenderedLine[], metadata: ISpanDistributionMetadata, restOfSpans: T.OneOfSpans[]): void {
        this.scrollHeight += this.currentContentLine!.lineHeight;
        if (this.style.textOverflow === 'truncate' &&
            !this.editor?.inInteractiveMode &&
            this.scrollHeight > this.height - (this.style.padding.top + this.style.padding.bottom!)) {
            this.reachedTruncationLine = true;
            const lastLine = textLines.pop()!;
            this.currentContentLine = textLines.pop()!;
            if (!this.currentContentLine) {
                this.currentContentLine = {
                    spans: [{ type: T.SpanType.End, content: 'END', top: 0, left: 0, width: 0, height: 0, lineHeight: 0, attributes: {} }],
                    lineHeight: 0,
                    maxFontSize: 0,
                    trailingSpaceWidth: 0,
                    lineWidth: 0,
                    characterWidth: 0,
                    endsWithNewline: false,
                    isLastLine: false,
                };
                textLines.splice(0, Infinity, this.currentContentLine);
                return;
            }
            this.currentContentLine.spans.push(...lastLine.spans, ...restOfSpans);
            textLines.push(this.currentContentLine);
            this.truncateCurrentContentLine(textLines);
            metadata.shouldAbort = true;
        }
    }

    private appendSpanToIntrospecter(span: ITypeContentStyle): HTMLSpanElement {
        const spanElement = document.createElement('span');
        spanElement.style.whiteSpace = 'nowrap';

        if (span.type === T.SpanType.End) {
            spanElement.innerHTML = '\u200B';
        }
        else {
            this.applyStyleOnElement({ ...{ uppercase: this.style.uppercase }, ...span.style }, spanElement);
            if (span.type === T.SpanType.Space || span.type === T.SpanType.Newline) {
                spanElement.style.whiteSpace = 'pre';
                spanElement.innerHTML = span.content;
            }
            else {
                spanElement.innerHTML = encodeHtml(span.content);
            }
        }

        spanElement.style.display = 'inline-block';
        // DEBUG: Comment this line to debug
        this.textIntrospecter.innerHTML = '';
        this.textIntrospecter.appendChild(spanElement);
        return spanElement;
    }

    private introspectSpan(span: T.OneOfSpans, useDom: boolean): ISize {
        if (useDom) {
            const spanElement = this.appendSpanToIntrospecter(span);
            const boundingRect = spanElement.getBoundingClientRect();
            return {
                width: boundingRect.width,
                height: boundingRect.height,
            };
        }
        else {
            return {
                width: span.width * this.fontSizeDiff,
                height: span.lineHeight * this.fontSizeDiff,
            };
        }
    }

    suspendNextBlur(): void {
        this.editor!.__suspendNextBlur = true;
    }

    addBlurSuspensionElement(htmlElement: HTMLElement): void {
        const suspendBlur = () => {
            this.editor!.__suspendNextBlur = true;
        };
        htmlElement.addEventListener('mousedown', suspendBlur);
        this.blurSuspensionListeners.push({ element: htmlElement, callback: suspendBlur });
    }

    removeBlurSuspensionElement(htmlElement: HTMLElement): void {
        for (const listener of this.blurSuspensionListeners) {
            if (listener.element === htmlElement) {
                htmlElement.removeEventListener('mousedown', listener.callback);
                break;
            }
        }
    }

    clearBlurSuspensionElements(): void {
        for (const listener of this.blurSuspensionListeners) {
            listener.element.removeEventListener('mousedown', listener.callback);
        }
    }

    rerenderText(): void {
        this.rootElement.style.fontSize = this.resizedElementFontSize + 'px';
        this.renderTextLines();
        this.removeDecorationLayers();
        if (this.renderUnderscoreLayer) {

            this.renderDecorationLayer('decoration-background-line', this.lineToSpanDecorationBackgroundLineElementMap, '-2');
            for (let line = 0; line < this.textLines.length; line++) {
                const textLine = this.textLines[line];
                const lineElement = this.lineToSpanDecorationBackgroundLineElementMap.get(line)!;
                for (let spanIndex = 0; spanIndex < textLine.spans.length; spanIndex++) {
                    const span = textLine.spans[spanIndex];
                    if (span.attributes.styleIndex === undefined) {
                        continue;
                    }
                    if (span.type === T.SpanType.Newline) {
                        continue;
                    }
                    const backgroundElement = document.createElement('div');
                    backgroundElement.style.position = 'absolute';
                    backgroundElement.style.top = '0';
                    backgroundElement.style.left = Math.round(span.left) + 'px';
                    backgroundElement.style.height = '100%';
                    backgroundElement.style.width = Math.round(span.width) + 'px'; // Round value, otherwise the first border dot becomes wide.
                    backgroundElement.style.backgroundColor = '#f6f6f6';

                    lineElement.appendChild(backgroundElement);
                }
            }

            this.renderDecorationLayer('decoration-line', this.lineToSpanDecorationLineElementMap);
            for (let line = 0; line < this.textLines.length; line++) {
                const textLine = this.textLines[line];
                const lineElement = this.lineToSpanDecorationLineElementMap.get(line)!;
                lineElement.style.display = 'block';
                for (let spanIndex = 0; spanIndex < textLine.spans.length; spanIndex++) {
                    const span = textLine.spans[spanIndex];
                    if (span.attributes.styleIndex === undefined) {
                        continue;
                    }
                    if (span.type === T.SpanType.Newline) {
                        continue;
                    }
                    const underscoreElement = document.createElement('div');
                    underscoreElement.style.position = 'absolute';
                    underscoreElement.style.top = '0';
                    underscoreElement.style.left = Math.round(span.left) + 'px';
                    underscoreElement.style.height = '100%';
                    underscoreElement.style.width = Math.round(span.width) + 'px'; // Round value, otherwise the first border dot becomes wide.
                    let borderWidth = 0;
                    while (borderWidth + 3 < span.width) {
                        const dotElement = document.createElement('div');
                        dotElement.style.width = '1px';
                        dotElement.style.height = '1px';
                        dotElement.style.position = 'absolute';
                        dotElement.style.bottom = '0';
                        dotElement.style.left = `${borderWidth}px`;
                        dotElement.style.backgroundColor = 'var(--ui-color-primary)';
                        borderWidth += 3;
                        underscoreElement.appendChild(dotElement);
                    }

                    if (span.attributes.shouldRenderNumber) {
                        const numberElement = document.createElement('div');
                        const styleIndex = span.attributes.styleIndex;
                        numberElement.innerHTML = styleIndex && styleIndex > 0 ? styleIndex + '' : '?';
                        numberElement.style.position = 'absolute';
                        numberElement.style.right = '-5px';
                        numberElement.style.top = '-4px';
                        numberElement.style.fontSize = '8px';
                        numberElement.style.fontStyle = 'italic';
                        numberElement.style.color = 'var(--ui-color-primary)';
                        numberElement.style.display = this.editor!.hasFocus ? 'block' : 'none';
                        numberElement.className = 'number';
                        underscoreElement.appendChild(numberElement);
                    }
                    lineElement.appendChild(underscoreElement);
                }
            }
        }

        if (this.editor) {
            this.renderDecorationLayer('selection-line', this.editor!.selection.lineToSelectionLineElementMap);
        }
    }

    normalizeTextLineSpans(): void {
        const newTextLines: T.IRenderedLine[] = [];
        let currentTextLine: T.IRenderedLine;
        currentTextLine = {
            spans: [],
            lineHeight: 0,
            maxFontSize: 0,
            lineWidth: 0,
            trailingSpaceWidth: 0,
            characterWidth: 0,
            endsWithNewline: false,
            isLastLine: false,
        };
        let lastSpan: T.OneOfRenderedSpans | undefined;
        for (let line = 0; line < this.textLines.length; line++) {
            const textLine = this.textLines[line];
            for (const span of textLine.spans) {
                if (span.type === T.SpanType.Ellipsis || span.type === T.SpanType.Composition) {
                    lastSpan = span;
                    currentTextLine.spans.push(span);
                    continue;
                }
                if (span.content === '') {
                    continue;
                }
                if (lastSpan && this.isMergeable(lastSpan, span)) {
                    lastSpan.content += span.content;
                    continue;
                }
                else {
                    lastSpan = span;
                    currentTextLine.spans.push(span);
                    if (isContentSpan(span)) {
                        for (const property of Object.keys(N.characterPropertyToUnitMap)) {
                            if (hasSameStyleProperty(property, span.style, this.style)) {
                                delete span.style[property];
                            }
                        }
                    }
                }
            }
        }

        // Set current shouldRenderNumber flag
        if (typeof process !== 'undefined' && process.env.STUDIO_JS) {
            if (this.renderUnderscoreLayer) {
                let previousSpan: T.OneOfRenderedSpans | undefined;
                const singlyIndexed = currentTextLine.spans.every(span => span.attributes.styleIndex === undefined || span.attributes.styleIndex === 1);

                // If the only index is "1" don't show anything
                if (singlyIndexed) {
                    currentTextLine.spans.forEach(span => span.attributes.shouldRenderNumber = false);
                }
                // Make sure only last span with same index renders number
                else {
                    for (let spanIndex = 0; spanIndex < currentTextLine.spans.length; spanIndex++) {
                        const span = currentTextLine.spans[spanIndex];
                        const index = span.attributes.styleIndex;
                        if (previousSpan) {
                            const previousIndex = previousSpan.attributes.styleIndex;
                            if (previousIndex === index) {
                                previousSpan.attributes.shouldRenderNumber = false;
                            }
                            else if (previousIndex !== undefined) {
                                previousSpan.attributes.shouldRenderNumber = true;
                            }
                        }
                        previousSpan = span;
                    }
                    previousSpan = undefined;
                }
            }
        }
        newTextLines.push(currentTextLine);
        this.textLines = newTextLines;
        this.spans = this.getSpansFromTextLines();
    }

    isMergeable(source: T.OneOfRenderedSpans, target: T.OneOfSpans): boolean {
        if (source.type === T.SpanType.Newline || source.type === T.SpanType.End) {
            return false;
        }
        if (source.type !== target.type) {
            return false;
        }
        if (!hasSameStyleIds(source.styleIds, target.styleIds)) {
            return false;
        }
        if (!deepEqual(source.__previousStyleIds, target.__previousStyleIds)) {
            return false;
        }
        return this.hasSameStyleAndAttributes(source, target);
    }

    private hasSameStyleAndAttributes(source: T.OneOfContentSpans, target: T.OneOfContentSpans): boolean {
        const sourceStyle = omit(source.style, '__fontFamilyId');
        const targetStyle = omit(target.style, '__fontFamilyId');
        if (Object.keys(sourceStyle).length !== Object.keys(targetStyle).length) {
            return false;
        }
        for (const property in sourceStyle) {
            if (!hasSameStyleProperty(property as keyof T.ICharacterProperties, sourceStyle, targetStyle)) {
                return false;
            }
        }
        const sourceAttributes = omit(source.attributes, 'shouldRenderNumber');
        const targetAttributes = omit(target.attributes, 'shouldRenderNumber');
        if (Object.keys(sourceAttributes).length !== Object.keys(targetAttributes).length) {
            return false;
        }
        for (const attribute in sourceAttributes) {
            if (!deepEqual(source.attributes[attribute], target.attributes[attribute])) {
                return false;
            }
        }
        return true;
    }

    expandVariableSpans(spans: T.OneOfSpans[]): T.OneOfSpans[] {
        const expandedSpans: T.OneOfSpans[] = [];
        for (let i = 0; i < spans.length; i++) {
            if (spans[i].type === T.SpanType.Variable) {
                const span = spans[i] as T.IVariableSpan;

                /**
                 * Store the span and give it a temporary ID.
                 * We then resolve the variable to the correct feed data so that the
                 * text resize the actual value rather than the variable.
                 * The correct ID is then set in renderTextLines.
                 */
                if (!span.style.variable!.spanId || !this.variableSpanMap.get(span.style.variable!.spanId!)) {
                    span.style.variable!.spanId = span.style.variable!.spanId || '' + Date.now();
                    this.variableSpanMap.set(span.style.variable!.spanId!, {
                        style: span.style!,
                        span: span
                    });
                }

                const variableSpan = this.variableSpanMap.get(span.style.variable!.spanId!)!;
                if (!this.editor?.inInteractiveMode && !this.renderUnderscoreLayer) {
                    const feedSpans = createSpansFromString(this.feedStore.getFeedValue(
                        variableSpan!.style.variable!,
                        span.style.variable!.spanId!,
                        this.option.viewElement
                    ).value as string, {}, span.style, this.copyStyle);
                    expandedSpans.push(...feedSpans);
                }
                else {
                    span.content = T.VARIABLE_PREFIX + variableSpan.style.variable!.path;
                    expandedSpans.push(span);
                }
                continue;
            }
            expandedSpans.push(spans[i]);
        }
        return expandedSpans;
    }

    private getElementTextProperties(): Partial<T.ITextProperties> {
        return Object.assign(pick(this.style,
            'font',
            'characterSpacing',
            'lineHeight',
            'textColor',
            // 'uppercase',
            // 'strikethrough',
            // 'underline',
            'textOverflow',
            'textShadows',
            'padding',
            'maxRows',
            'horizontalAlignment',
            'verticalAlignment',
        ), { fontSize: this.resizedElementFontSize });
    }

    distributeAllSpansAcrossTextLines(spans: T.OneOfSpans[]): void {
        this.resizedElementFontSize = this.style.fontSize!;
        const elementTextProperties = this.getElementTextProperties();
        const textIntrospecter = this.textIntrospecter;
        textIntrospecter.innerHTML = '';
        this.applyStyleOnElement(elementTextProperties, textIntrospecter, /*useRelativeFontSize*/ false);

        if (elementTextProperties.textOverflow === 'truncate') {
            textIntrospecter.innerHTML = '…';
            const spanSize = this.introspectSpan({
                type: T.SpanType.Word,
                content: '…',
                lineHeight: 0,
                style: {},
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                attributes: {},
                styleIds: new Map<string, string>(),
                __previousStyleIds: this.editorState ? [] : undefined,
                __previousStyleIdToHistoryIndexMap: this.editorState ? new Map<string, number>() : undefined,
            }, /*useDom*/ true);
            this.ellipsisWidth = spanSize.width;
        }

        const textLines: T.IRenderedLine[] = [];
        this.resetTextMetadata();
        const metadata: ISpanDistributionMetadata = {
            shouldAbort: false,
        };
        for (let i = 0; i < spans.length; i++) {
            if (metadata.shouldAbort) {
                break;
            }
            this.distributeOneSpanAcrossTextLines(spans[i], textLines, metadata, /*useDom*/ true, spans.slice(i + 1));
        }
        this.textLines = textLines;
    }

    private resetTextMetadata(): void {
        this.scrollHeight = 0;
        this.longestLine = 0;
        this.wordWidth = 0;
        this.widestWord = 0;
        this.rows = 1;
        this.reachedTruncationLine = false;
        this.currentContentLine = undefined;
    }

    private redistributeAllSpansAcrossLines(spans: T.OneOfSpans[]): void {
        const textLines: T.IRenderedLine[] = [];
        this.resetTextMetadata();
        const metadata: ISpanDistributionMetadata = {
            shouldAbort: false,
        };
        for (let i = 0; i < spans.length; i++) {
            if (metadata.shouldAbort) {
                break;
            }
            this.distributeOneSpanAcrossTextLines(spans[i] as T.OneOfSpans, textLines, metadata, /*useDom*/ false, spans.slice(i + 1));
        }
        this.textLines = textLines;
    }

    private getNegativeLineHeightMargin(textLine: T.IRenderedLine): number {
        return -(textLine.lineHeight - textLine.maxFontSize * this.resizedElementFontSize) / 2;
    }

    renderTextLines(): void {
        const textProperties = this.getElementTextProperties();
        this.applyStyleOnElement(textProperties, this.rootElement, /*isRootElement*/ false);

        this.textElement!.innerHTML = '';
        this.textElement.classList.add('invisible-selection');
        this.textLineElementToLineMap = new WeakMap<HTMLDivElement, number>();
        this.lineToTextLineElementMap = new Map<number, HTMLDivElement>();
        this.variableSpanMap = new Map<string, { span: T.OneOfContentSpans, style: Partial<T.ICharacterProperties> }>();
        if (!this.editor?.inInteractiveMode && this.style.textOverflow === 'scroll') {
            this.rootElement.style.overflow = 'auto';
        }
        else {
            this.rootElement.style.overflow = 'visible';
        }
        this.centerElement.style.alignItems = getVerticalFlexAlignment(this.style.verticalAlignment);
        this.centerElement.style.justifyContent = getHorizontalFlexAlignment(this.style.horizontalAlignment);
        this.textElement.style.textAlign = this.style.horizontalAlignment;
        this.textElement.style.lineHeight = this.style.lineHeight + '';

        for (let line = 0; line < this.textLines.length; line++) {
            const textLine = this.textLines[line];
            const lineFragment = document.createDocumentFragment();
            const lineElement = document.createElement('div');
            lineElement.className = 'text-line';

            // Use block and explicit height to prevent white space underneath it when children(spans)
            // being wider than block width. This becomes apparant when font size becomes smaller and
            // there is a lot of characters.
            switch (this.style.horizontalAlignment) {
                case 'left':
                    lineElement.style.justifyContent = 'flex-start';
                    break;
                case 'center':
                    lineElement.style.justifyContent = 'center';
                    break;
                case 'right':
                    lineElement.style.justifyContent = 'flex-end';
                    break;
            }
            lineElement.style.display = 'flex';
            lineElement.style.alignItems = 'baseline';
            lineElement.style.height = Math.max(1, textLine.lineHeight) + 'px';
            lineElement.style.whiteSpace = 'nowrap';
            lineElement.style.fontSize = (textLine.maxFontSize * this.resizedElementFontSize!) + 'px';

            if (this.style.characterSpacing) {
                lineElement.style.letterSpacing = (this.style.characterSpacing) + 'em';
            }
            if (textLine.trailingSpaceWidth > 0) {
                switch (this.style.horizontalAlignment) {
                    case 'center':
                        lineElement.style.paddingLeft = textLine.trailingSpaceWidth + 'px';
                        break;
                    case 'right':
                        lineElement.style.marginRight = -textLine.trailingSpaceWidth + 'px';
                        break;
                }
            }

            if (this.style.verticalAlignment === 'top' && line === 0) {
                lineElement.style.marginTop = this.getNegativeLineHeightMargin(textLine) + 'px';
            }
            else if (this.style.verticalAlignment === 'bottom' && textLine.isLastLine) {
                lineElement.style.marginBottom = this.getNegativeLineHeightMargin(textLine) + 'px';
            }
            this.textLineElementToLineMap.set(lineElement, line);
            this.lineToTextLineElementMap.set(line, lineElement);
            lineFragment.appendChild(lineElement);
            const spanId = 't-' + line + '-';
            let column = 0;
            forEachSpan(textLine, (span, spanIndex) => {
                if (textLine.spans.length !== 1 && span.type === T.SpanType.End) {
                    return;
                }
                const spanElement = document.createElement('span');

                if (this.renderUnderscoreLayer) {
                    if (span.attributes.shouldRenderNumber) {
                        spanElement.style.marginRight = STYLE_INDEX_MARGIN_RIGHT + 'px';
                    }
                }

                let shouldSkipRenderDecoration = false;
                if (span.type === T.SpanType.Space) {
                    if (spanIndex === textLine.spans.length - 1 ||
                        (spanIndex === textLine.spans.length - 2 && textLine.spans[textLine.spans.length - 1].type === T.SpanType.Newline)) {
                        shouldSkipRenderDecoration = true;
                    }
                }

                // We need 'inline-block' here, otherwise a parent's element's width is not the
                // same as the sum of its children. Though there is a problem in Chrome for getting
                // the caret position sometimes on inline-block. Right now we only experienced it on
                // the translation panel. So we are just removing inline-block there.
                // if (!this.renderUnderscoreLayer) {
                    spanElement.style.display = 'inline-block';
                // }
                spanElement.setAttribute('data-type', this.getStringSpanType(span.type));
                spanElement.id = (this.rootElement.id || this.editor?.keyboardBindings.inputId) + '-' + spanId + spanIndex;

                if (span.type === T.SpanType.Variable) {
                    spanElement.setAttribute('data-variable', span.style.variable!.path);
                    if (span.style.variable!.spanId !== spanElement.id) {
                        this.variableSpanMap.delete(span.style.variable!.spanId!);
                        span.style.variable!.spanId = spanElement.id;
                        this.variableSpanMap.set(span.style.variable!.spanId!, {
                            style: span.style!,
                            span
                        });
                    }
                    else if (!this.variableSpanMap.get(spanElement.id)) {
                        span.style.variable!.spanId = spanElement.id;
                        this.variableSpanMap.set(spanElement.id, {
                            style: span.style!,
                            span
                        });
                    }

                    span.style.variable!.spanId = spanElement.id;

                    if (!this.renderUnderscoreLayer) {
                        this.feedStore.addFeedElement(spanElement.id, span.style.variable!, this.option.viewElement);
                    }
                }

                spanElement.addEventListener('click', this.onClick);

                if (span.type === T.SpanType.End) {
                    spanElement.style.height = this.editor?.currentStyle.lineHeight! * this.resizedElementFontSize + 'px';
                    spanElement.innerHTML = '&#8203;'; // Zero-width-space
                }
                else if (span.type === T.SpanType.Ellipsis) {
                    spanElement.innerHTML = '...';
                }
                else {
                    if (span.type === T.SpanType.Newline) {
                        spanElement.innerHTML = ' ';
                        spanElement.style.whiteSpace = 'pre';
                        switch (this.style.horizontalAlignment) {
                            case 'center':
                                // Compensate for space character
                                spanElement.style.width = span.width + 'px';
                                break;
                            case 'left':
                            case 'right':
                                spanElement.style.width = span.width + 'px';
                                break;
                        }
                    }
                    else if (span.type === T.SpanType.Space) {
                        spanElement.style.whiteSpace = 'pre';
                        spanElement.innerHTML = span.content;
                    }
                    else if (span.type === T.SpanType.Composition) {
                        spanElement.style.width = span.width + 'px';
                        spanElement.innerHTML = '&#8203;';
                        this.editor!.keyboardBindings.compositionSpanElement = spanElement;
                    }
                    else {
                        spanElement.innerHTML = encodeHtml(span.content);
                    }
                    const textDecoration: string[] = [];

                    if (this.style.underline) {
                        textDecoration.push('underline');
                    }
                    if (this.style.strikethrough) {
                        textDecoration.push('strikethrough');
                    }
                    const spanStyle = Object.assign({}, {
                        underline: this.style.underline,
                        strikethrough: this.style.strikethrough,
                        uppercase: this.style.uppercase,
                    }, span.style);

                    // Don't decorate spaces if it's in the end of the line
                    if (shouldSkipRenderDecoration) {
                        delete spanStyle.underline;
                        delete spanStyle.strikethrough;
                    }

                    this.applyStyleOnElement(Object.assign({ fontSize: 1 }, spanStyle), spanElement);
                }
                if (isContentSpan(span)) {
                    span.line = line;
                    span.column = column;
                    column += span.content.length;
                }
                lineElement.appendChild(spanElement);
            });

            this.textElement!.appendChild(lineFragment);
        }
    }

    applyStyleOnElement(style: Partial<T.ICharacterProperties>, element: HTMLElement, useRelativeFontSize: boolean = true): void {
        const styleAttribute = element.style;
        let handledTextDecorationProperty = false;
        const characterStyleProperties = [...Object.keys(omit(N.characterPropertyToUnitMap, '__fontFamilyId')), 'padding'];

        for (const property of characterStyleProperties) {
            switch (property) {
                case 'font':
                    if (style.font) {
                        const font = getFontStyleCSS(style.font, element);
                        Object.assign(styleAttribute, font);
                    }
                    else {
                        styleAttribute.fontFamily = '';
                        styleAttribute.fontStyle = 'normal';
                        styleAttribute.fontWeight = '400';
                    }
                    break;
                case 'textColor':
                    styleAttribute.color = style.textColor
                        ? style[property]!.toRGBA()
                        : '';
                    break;
                case 'fontSize':
                    styleAttribute.fontSize = style.fontSize !== undefined
                        ? useRelativeFontSize
                            ? (this.resizedElementFontSize * (style[property] as number)) + 'px'
                            : this.resizedElementFontSize + 'px'
                        : '';
                    break;
                case 'underline':
                case 'strikethrough':
                    if (handledTextDecorationProperty) {
                        break;
                    }
                    const cssTextDecoration: string[] = [];
                    if (style.underline) {
                        cssTextDecoration.push('underline');
                    }
                    if (style.strikethrough) {
                        cssTextDecoration.push('line-through');
                    }
                    styleAttribute.textDecoration = cssTextDecoration.length > 0 ? cssTextDecoration.join(' ') : '';
                    handledTextDecorationProperty = true;
                    break;
                case 'uppercase':
                    if (style.uppercase) {
                        styleAttribute.textTransform = 'uppercase';
                    }
                    else {
                        styleAttribute.textTransform = '';
                    }
                    break;
                case 'characterSpacing':
                    styleAttribute.letterSpacing = style.characterSpacing !== undefined ? style.characterSpacing + 'em' : '';
                    break;
                case 'lineHeight':
                    styleAttribute.lineHeight = style.lineHeight !== undefined
                        ? useRelativeFontSize
                            ? style[property] + 'em'
                            : style[property] + ''
                        : 'none';
                    break;
                case 'textShadows':
                    styleAttribute.textShadow = style.textShadows
                        ? textShadowsCSSValue(style[property] as ITextShadow[])
                        : '';
                    break;
                case 'variable':
                    if (style.variable) {
                        if (this.editor?.inInteractiveMode) {
                            styleAttribute.color = 'var(--variable-artifact-color)';
                            styleAttribute.backgroundColor = 'rgba(235, 235, 235, 0.7)';
                            styleAttribute.cursor = 'var(--variable-artifact-cursor)';
                            styleAttribute.fontFamily = `'Open Sans', ${FALLBACK_FONT_FAMILY}`;
                        }
                        else {
                            styleAttribute.color = styleAttribute.color || '';
                            styleAttribute.cursor = '';
                            styleAttribute.fontFamily = styleAttribute.fontFamily || '';
                        }
                    }
                    break;
                case 'padding':
                    if (style['padding']) {
                        styleAttribute.paddingTop = style[property].top + 'px';
                        styleAttribute.paddingLeft = style[property].left + 'px';
                        styleAttribute.paddingRight = style[property].right + 'px';
                        styleAttribute.paddingBottom = style[property].bottom + 'px';
                    }
                    else {
                        styleAttribute.paddingTop = '';
                        styleAttribute.paddingLeft = '';
                        styleAttribute.paddingRight = '';
                        styleAttribute.paddingBottom = '';

                    }
                    break;
                default:
                    styleAttribute[property] = style[property];
                    break;
            }
        }
    }

    private getStringSpanType(type: T.SpanType): string {
        switch (type) {
            case T.SpanType.Newline:
                return 'newline';
            case T.SpanType.Space:
                return 'space';
            case T.SpanType.Word:
                return 'text';
            case T.SpanType.Ellipsis:
                return 'ellipsis';
            case T.SpanType.End:
                return 'end';
            case T.SpanType.Variable:
                return 'variable';
            case T.SpanType.Composition:
                return 'composition';
        }
        throw new Error('Unknown span type.');
    }

    private renderDecorationLayer(className: string, store: Map<number, HTMLDivElement>, zIndex: string = '-1'): void {
        store.clear();
        const textElementClone = this.textElement.cloneNode();
        const centerElementClone = this.centerElement.cloneNode() as HTMLDivElement;
        centerElementClone.style.position = 'absolute';
        centerElementClone.style.top = '0';
        centerElementClone.style.left = '0';
        centerElementClone.style.paddingLeft = this.style.padding.left + 'px';
        centerElementClone.style.paddingRight = this.style.padding.right + 'px';
        centerElementClone.style.paddingTop = this.style.padding.top + 'px';
        centerElementClone.style.paddingBottom = this.style.padding.bottom + 'px';
        centerElementClone.style.width = `calc(100% - ${this.style.padding.left}px - ${this.style.padding.right}px)`;
        centerElementClone.style.pointerEvents = 'none';

        this.rootElement.prepend(centerElementClone);
        centerElementClone!.appendChild(textElementClone);
        this.centerElements.push(centerElementClone);
        const boundingRect = this.rootElement.getBoundingClientRect();
        const rootElementTop = boundingRect.top;
        const rootElementLeft = boundingRect.left;
        for (let line = 0; line < this.textLines.length; line++) {
            const textLineElement = this.lineToTextLineElementMap.get(line)!;
            const textLine = this.textLines[line];
            const lineFragment = document.createDocumentFragment();
            const lineElement = document.createElement('div');
            lineElement.className = className;
            lineElement.style.width = this.width + 'px';
            lineElement.style.position = 'absolute';
            lineElement.style.zIndex = zIndex;
            const lineElementBoundingRect = textLineElement.getBoundingClientRect();
            lineElement.style.top = (lineElementBoundingRect.top - rootElementTop) + 'px';
            lineElement.style.left = (lineElementBoundingRect.left - rootElementLeft) + 'px';
            lineElement.style.height = textLine.lineHeight + 'px';
            switch (this.style.horizontalAlignment) {
                case 'center':
                    lineElement.style.display = 'center';
                    break;
            }
            store.set(line, lineElement);
            lineFragment.appendChild(lineElement);
            textElementClone.appendChild(lineFragment);
        }
    }

    onClick = (event: MouseEvent): void => {
        if (!this.editor?.inInteractiveMode && (!window.bfstudio || (window.bfstudio && window.bfstudio!.inShowcaseMode))) {
            const id = (event.currentTarget as HTMLElement)!.id;
            let feededElement = this.feedStore.elements.get(id);

            if (!feededElement) {
                const variableSpans: T.IVariableSpan[] = [];
                for (const span of this.spans) {
                    if (isContentSpan(span) && span.style?.variable) {
                        variableSpans.push(span as T.IVariableSpan);
                        if (variableSpans.length > 1) {
                            break;
                        }
                    }
                }

                const isSingleVariable = variableSpans.length === 1 ? true : false;

                if (isSingleVariable) {
                    feededElement = this.feedStore.elements.get(variableSpans[0].style.variable!.spanId!);
                }
            }

            if (feededElement) {
                const feedValue = this.feedStore.getFeedValue(feededElement.feed, id, feededElement.element!);
                event.preventDefault();
                event.stopPropagation();
                this.emit('spanClicked', [event, feedValue.targetUrl]);
            }
        }
    }

    isOfNodeSpanType(node: Node | HTMLElement, type: T.SpanType): boolean {
        if (node.nodeType === 3 && node.parentNode) {
            node = node.parentNode as HTMLElement;
        }
        else {
            return false;
        }
        if ((node as HTMLElement).dataset) {
            return this.getSpanTypeString((node as HTMLElement).dataset.type!) === type;
        }
        else {
            return false;
        }
    }

    getSpanTypeString(type: string): T.SpanType {
        switch (type) {
            case 'newline':
                return T.SpanType.Newline;
            case 'space':
                return T.SpanType.Space;
            case 'text':
                return T.SpanType.Word;
            case 'end':
                return T.SpanType.End;
            case 'variable':
                return T.SpanType.Variable;
            case 'composition':
                return T.SpanType.Composition;
        }
        throw new Error('Unknown span type.');
    }

    /* private */ throttleUndoSnapshot = (): void => {
        this.pendingSnapshots++;
        this.lastSnapshotTimestamp = Date.now();
        if (!this.firstSnapshotFromBurst) {
            this.firstSnapshotFromBurst = this.createSnapshot();
            this.undoSnapshots.push(this.firstSnapshotFromBurst!);
        }
        setTimeout(this.tryAppendFirstSnapshotFromBurst, 500);
    }

    private tryAppendFirstSnapshotFromBurst = (): void => {
        this.pendingSnapshots--;
        if (Date.now() > this.lastSnapshotTimestamp + 450 && this.pendingSnapshots <= 0) {
            this.firstSnapshotFromBurst = undefined;
        }
    }

    onUndoTextChange = (): void => {
        const snapshot = this.undoSnapshots.pop();
        if (snapshot) {
            const s = this.createSnapshot();
            this.redoSnapshots.push(s);
            this.useSnapshot(snapshot);
        }
    }

    onRedoTextChange = (): void => {
        const snapshot = this.redoSnapshots.pop();
        if (snapshot) {
            this.undoSnapshots.push(this.createSnapshot());
            this.useSnapshot(snapshot);
        }
    }

    createSnapshot = (selection?: TextSelection): ISnapshot => {
        return {
            currentStyle: cloneDeep(this.editor!.currentStyle),
            textLines: cloneDeep(this.textLines),
            spans: cloneDeep(this.spans),
            selection: cloneDeep(selection || this.selection),
        };
    }

    /* private */ onEditStart = (): void => {
        this.snapshots.push(this.createSnapshot());
    }

    /* private */ onEditEnd = (hasChanges: boolean): void => {
        if (hasChanges) {
            const snapshot = this.snapshots.pop();
            if (snapshot) {
                this.undoSnapshots.push(snapshot);
            }
        }
        this.redoSnapshots = [];
        this.snapshots = [];
    }

    private useSnapshot(snapshot: ISnapshot): void {
        const { currentStyle, spans, textLines, selection } = snapshot;
        this.editor!.currentStyle = currentStyle;
        this.spans = spans;
        this.textLines = textLines;
        this.selection = selection;
        this.rerender();
        this.emit('change', [this.getText()]);
        this.emit('historychange');
    }
}

__inject(S.EDITOR_STATE, { optional: true }, RichText, 'editorState', 3);
__inject(S.RICH_TEXT_EDITOR_SERVICE, { optional: true }, RichText, 'editor', 4);

interface ISnapshot {
    currentStyle: Partial<T.ICharacterProperties>;
    spans: T.OneOfSpans[];
    textLines: T.IRenderedLine[];
    selection?: TextSelection;
}

/**
 * Get font style. If no font is provided get clalculated default.
 * Also make sure fallback font is provided.
 */
function getFontStyleCSS(fs: Partial<IFontStyle> = {}, element?: HTMLElement): IFontStyleCSS {

    const fontStyle = fs.style || 'normal';
    const fontWeight = fs.weight || 400;
    let fontFamily = fs.id ? `"${fs.id}"` : 'unset';

    // When no font family is set try to calculate which one to use
    if (fontFamily === 'unset' && element) {
        const computedStyle = window.getComputedStyle(element).fontFamily;
        if (computedStyle) {
            fontFamily = computedStyle;
        }
    }

    // Add fallback font if only one fontFamily is provided.
    if (fontFamily !== 'unset' && fontFamily.indexOf(',') === -1) {
        fontFamily += ', ' + FALLBACK_FONT_FAMILY;
    }

    return {
        fontFamily,
        fontStyle,
        fontWeight
    };
}

interface IFontStyleCSS {
    fontFamily: string;
    fontStyle: 'italic' | 'normal';
    fontWeight: number;
}
